/**
 * Per-service detail panel (mockup `selectSurvNet` L10848-10952).
 *
 * Sections (top → bottom):
 *   1. Header: ← Surveillance · {service.label}, sub: "{total} sensors · {n} online"
 *   2. Service Info: Type / Total / Online / Offline / Avg Latency / Targets / Trust
 *   3. Coverage Models: collapsible row per AGL — clicking activates that
 *      model's polygon on the map (`useSurvStore.setCovModel`). Detail
 *      under each row lists contributing sensors and offers a Regenerate
 *      button when not-accurate (mockup `_toggleCovDetail` L10963).
 *   4. Stations: card per sensor with state dot + meta + RTT/Lat/RSSI stats.
 */
import { useState } from 'react';
import { ArrowLeft, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';
import { selectServiceById, useSurvStore } from '@/lib/store/surv';
import { SERVICE_LABEL, type ServiceId } from '@/lib/mqtt/schemas/surveillance';
import { useShallow } from 'zustand/shallow';
import { Badge } from '@/components/ui/badge';
import { Section, SectionGroup } from '@/components/Section';
import { InfoRow } from '@/components/InfoRow';
import { cn } from '@/lib/utils';
import { can } from '@/lib/auth/access';
import { useCurrentRole } from '@/lib/store/auth';

const STATE_COLOR = {
  live:     { dot: 'bg-(--color-green)',  text: 'text-(--color-green)',  label: 'Live' },
  degraded: { dot: 'bg-(--color-amber)',  text: 'text-(--color-amber)',  label: 'Degraded' },
  offline:  { dot: 'bg-(--color-text3)',  text: 'text-(--color-text3)',  label: 'Offline' },
} as const;

export function SurvDetail({ id }: { id: string }) {
  const setMode = useSurvStore((s) => s.setMode);
  const svc = useSurvStore(selectServiceById(id));
  // Live_Fused has no stations of its own; its inputs are what it is made of,
  // so the detail lists those instead.
  const fusedInputs = useSurvStore(useShallow((s) => s.fusedSelected));
  const activeCovIdx = useSurvStore((s) => s.activeCovModelIdx);
  const setCovModel = useSurvStore((s) => s.setCovModel);
  const regenCovModel = useSurvStore((s) => s.regenCovModel);
  // Surveillance is public read-only (ENG-279); regenerating is a write.
  const canRegen = can('surveillance:write', useCurrentRole());
  const [openIdx, setOpenIdx] = useState<number | null>(activeCovIdx);
  // Mockup `regenCovModel` (L10991) simulates a 1.5 s regenerate; track which
  // row is pending so the button can show the in-flight label.
  const [regenIdx, setRegenIdx] = useState<number | null>(null);

  const handleRegen = (modelIdx: number) => {
    setRegenIdx(modelIdx);
    setTimeout(() => {
      regenCovModel(id, modelIdx);
      setRegenIdx(null);
      toast.success(`Coverage model ${svc?.coverageModels[modelIdx]?.agl ?? ''} ft regenerated`);
    }, 1500);
  };

  if (!svc) return <div className="p-4 text-[0.769em] text-(--color-text3)">Service not found.</div>;

  const onlineSet = new Set(svc.stations.filter((s) => s.state === 'live').map((s) => s.sensor_id));

  const handleClickModel = (i: number) => {
    if (openIdx === i) {
      setOpenIdx(null);
      setCovModel(null);
    } else {
      setOpenIdx(i);
      setCovModel(i);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <header className="px-3 py-2.5 border-b border-(--color-border) shrink-0">
        <button
          onClick={() => setMode('fused')}
          className="flex items-center gap-1 text-(--color-green) text-[0.846em] font-semibold mb-1 hover:opacity-80"
        >
          <ArrowLeft size={13} /> Surveillance
        </button>
        <div className="flex items-center gap-2">
          <h2 className="text-base font-bold">{svc.label}</h2>
          <Badge variant={svc.type === 'Cooperative' ? 'info' : 'muted'}>{svc.type}</Badge>
        </div>
        <div className="text-[0.692em] text-(--color-text3) mt-0.5 font-mono">
          {svc.total_sensors} sensors · {svc.online_sensors} online
          {svc.total_sensors - svc.online_sensors > 0 && ` · ${svc.total_sensors - svc.online_sensors} offline`}
        </div>
      </header>

      <div className="flex-1 overflow-y-auto p-3">
        <SectionGroup>
          {/* ── Service Info ─────────────────────────────────────────────── */}
          <Section title="Service Info">
            <div className="rounded-md border border-(--color-border) bg-(--color-bg) px-3 py-2">
              <InfoRow label="Status" value={svc.service_state} valueClassName={
                svc.service_state === 'Active' ? 'text-(--color-green) font-bold'
                : svc.service_state === 'Degraded' ? 'text-(--color-amber) font-bold'
                : 'text-(--color-red) font-bold'
              } />
              <InfoRow label="Total Sensors" value={String(svc.total_sensors)} mono />
              <InfoRow label="Online" value={String(svc.online_sensors)} mono valueClassName="text-(--color-green) font-bold" />
              {svc.total_sensors - svc.online_sensors > 0 && (
                <InfoRow label="Offline" value={String(svc.total_sensors - svc.online_sensors)} mono valueClassName="text-(--color-amber) font-bold" />
              )}
              <InfoRow label="Avg Latency" value={svc.avg_latency_ms != null ? `${svc.avg_latency_ms} ms` : '—'} mono valueClassName={
                svc.avg_latency_ms != null && svc.avg_latency_ms <= 100 ? 'text-(--color-green) font-bold' : 'text-(--color-amber) font-bold'
              } />
              <InfoRow label="Visible Targets" value={String(svc.visibleTargets.length)} mono />
              <InfoRow label="Trust Level" value={svc.trust} mono valueClassName={
                svc.trust === 'High' ? 'text-(--color-green) font-bold' : 'text-(--color-amber) font-bold'
              } />
            </div>
          </Section>

          {/* ── Fused Inputs (Live_Fused only) ───────────────────────────── */}
          {svc.service_id === 'live_fused' && (
            <Section title="Fused Inputs">
              <p className="text-[0.692em] text-(--color-text3) -mt-1 mb-1.5 leading-snug">
                Live_Fused combines these streams. Its sensors and coverage models are theirs,
                pooled — the receivers themselves belong to the input services.
              </p>
              <div className="flex flex-wrap gap-1">
                {fusedInputs.map((inputId) => (
                  <span
                    key={inputId}
                    className="rounded-[3px] border border-(--color-border) bg-(--color-surface) px-1.5 py-[2px] font-mono text-[0.692em] text-(--color-text2)"
                  >
                    {SERVICE_LABEL[inputId as ServiceId] ?? inputId}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* ── Coverage Models ──────────────────────────────────────────── */}
          <Section title="Coverage Models">
            {svc.coverageModels.length === 0 && (
              <div className="text-[0.769em] text-(--color-text3) p-3 text-center rounded-md border border-dashed border-(--color-border)">
                No coverage models available
              </div>
            )}
            <div className="space-y-1.5">
              {svc.coverageModels.map((m, i) => {
                const allOnline = m.sensors.every((sid) => onlineSet.has(sid));
                const isOpen = openIdx === i;
                return (
                  <div key={i} className="rounded-md border border-(--color-border) bg-(--color-surface) overflow-hidden">
                    <button
                      onClick={() => handleClickModel(i)}
                      className={cn(
                        'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 text-left transition-colors',
                        isOpen ? 'bg-(--color-green-bg)' : 'hover:bg-(--color-bg)',
                      )}
                    >
                      <span className="text-[0.846em] font-bold font-mono">{m.agl} ft AGL</span>
                      <span className="flex-1 text-[0.692em] text-(--color-text3) truncate font-mono">
                        {m.sensors.length} sensors · polygon
                      </span>
                      <span className={cn(
                        'text-[0.615em] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded',
                        allOnline ? 'bg-(--color-green) text-(--color-on-accent)' : 'bg-(--color-amber) text-(--color-on-accent)',
                      )}>
                        {allOnline ? 'Accurate' : 'Not Accurate'}
                      </span>
                      <span className={cn('text-(--color-text3) text-[0.692em] transition-transform', isOpen && 'rotate-180')}>▼</span>
                    </button>
                    {isOpen && (
                      <div className="px-2.5 py-2 border-t border-(--color-border-lite) bg-(--color-bg)">
                        <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mb-1">Model details</div>
                        <div className="text-[0.692em] font-mono text-(--color-text2) mb-2">Created: {m.createdAt}</div>
                        <div className="text-[0.615em] font-bold uppercase tracking-wider text-(--color-text3) mb-1">Sensors in Model</div>
                        <ul className="space-y-0.5 mb-2">
                          {m.sensors.map((sid) => {
                            const online = onlineSet.has(sid);
                            return (
                              <li key={sid} className="flex items-center gap-1.5 text-[0.769em] font-mono">
                                <span className={cn('w-1.5 h-1.5 rounded-full', online ? 'bg-(--color-green)' : 'bg-(--color-red)')} />
                                <span>{sid}</span>
                                <span className={online ? 'text-(--color-green)' : 'text-(--color-red)'}>
                                  {online ? 'Online' : 'Offline'}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                        {svc.service_id === 'live_fused' ? (
                          // Fused coverage is inherited from the inputs, so
                          // regenerating belongs on the service that owns it —
                          // `regenCovModel` resolves via `byId` and would
                          // silently no-op here.
                          <div className="text-[0.692em] text-(--color-text3)">
                            Inherited from a fused input — regenerate it on that service.
                          </div>
                        ) : !allOnline && !canRegen ? (
                          <div className="text-[0.692em] text-(--color-text3)">
                            Some sensors aren't contributing — model may be out of date.
                          </div>
                        ) : !allOnline ? (
                          <button
                            type="button"
                            disabled={regenIdx === i}
                            onClick={(e) => { e.stopPropagation(); handleRegen(i); }}
                            className={cn(
                              'inline-flex items-center gap-1 px-2 py-1 rounded text-[0.692em] font-bold text-white transition-opacity',
                              regenIdx === i ? 'bg-(--color-text3) cursor-wait' : 'bg-(--color-amber) hover:opacity-90',
                            )}
                          >
                            <RefreshCcw size={10} className={regenIdx === i ? 'animate-spin' : ''} />
                            {regenIdx === i ? 'Regenerating…' : 'Regenerate Model'}
                          </button>
                        ) : (
                          <div className="text-[0.692em] text-(--color-green) font-semibold">
                            ✓ All sensors contributing — model is current
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Section>

          {/* ── Stations ─────────────────────────────────────────────────── */}
          <Section title="Stations">
            <div className="space-y-1.5">
              {svc.stations.length === 0 && (
                <div className="text-[0.769em] text-(--color-text3) p-3 text-center rounded-md border border-dashed border-(--color-border)">
                  {svc.service_id === 'live_fused'
                    ? 'Aggregate service — receivers belong to the fused inputs'
                    : 'Open source — no station data available'}
                </div>
              )}
              {svc.stations.map((st) => {
                const sc = STATE_COLOR[st.state];
                return (
                  <article
                    key={st.sensor_id}
                    className={cn(
                      'rounded-md border bg-(--color-surface) px-2.5 py-2 border-l-2',
                      st.state === 'live' && 'border-l-(--color-green)',
                      st.state === 'degraded' && 'border-l-(--color-amber)',
                      st.state === 'offline' && 'border-l-(--color-text3) opacity-75',
                      'border-(--color-border)',
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[0.846em] font-bold font-mono">{st.sensor_id}</span>
                      <div className="flex items-center gap-1.5">
                        <span className={cn('w-2 h-2 rounded-full', sc.dot)} />
                        <span className={cn('text-[0.769em] font-semibold', sc.text)}>{sc.label}</span>
                      </div>
                    </div>
                    <div className="text-[0.692em] font-mono text-(--color-text3) mt-1">
                      {st.lat.toFixed(4)}, {st.lng.toFixed(4)} · {st.label}
                    </div>
                    <div className="text-[0.692em] font-mono text-(--color-text2) mt-1">
                      {st.state === 'offline'
                        ? `Last seen ${new Date(st.last_seen).toLocaleTimeString()}`
                        : <>RTT {st.rtt_ms ?? '—'} · Lat {st.latency_ms ?? '—'}ms · RSSI {st.rssi ?? '—'} · Tgt {st.target_count}</>}
                    </div>
                  </article>
                );
              })}
            </div>
          </Section>
        </SectionGroup>
      </div>
    </div>
  );
}
